/**
 * @file BinLabelPrintDialog.tsx
 * @description Диалог «Этикетки ячеек» (phase19.22 шаг 5): ячейки, полки и
 *   стеллаж, копии, шаблон, «начать с N», коды, сводка листов и PDF.
 *
 * Подача — как у печати товара (`LabelPrintDialog`): шторка справа, на `<sm`
 * на весь экран, на `≥sm` 480px, кнопка «Сформировать PDF» прилипает к низу.
 * Переиспользуются `LabelCodeModeField`, `LabelCutLinesField`, `LabelPrintSummary` (с
 * `LabelSheetStartPicker` внутри) и `LabelPrintResult`. Сбой сервиса печати —
 * `Alert` с «Повторить», выбор при этом сохраняется.
 */

import { Info, Loader2, Printer } from "lucide-react";

import { BinLabelPrintFields } from "./BinLabelPrintFields";
import type { BinLabelScope } from "./bin-label-print";
import { useBinLabelPrintForm } from "./useBinLabelPrintForm";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { SectionLabel } from "../../ui/SectionLabel";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../ui/sheet";
import { LabelCodeModeField } from "../labels/LabelCodeModeField";
import { LabelCutLinesField } from "../labels/LabelCutLinesField";
import { LabelPrintResult } from "../labels/LabelPrintResult";
import { LabelPrintSummary } from "../labels/LabelPrintSummary";
import { LABEL_TEXT } from "../labels/label-texts";
import type { BinLabelPrintResult } from "../../../lib/api-labels";
import type { StorageBinDto } from "../../../lib/api-bins";

/** Совет по наклейке — склад влажный, этикетка должна жить годами. */
const STICK_HINT =
  "Ламинируйте скотчем — на складе влажно. Этикетки ячеек и полок клейте на торец " +
  "полки под ячейкой, стеллажную — на боковину стеллажа.";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouseId: string;
  /** Подзаголовок: название склада. */
  subtitle?: string;
  selected: StorageBinDto[];
  scope: BinLabelScope;
  onPrinted?: (result: BinLabelPrintResult) => void;
}

export function BinLabelPrintDialog({ open, onOpenChange, warehouseId, subtitle, selected, scope, onPrinted }: Props) {
  const form = useBinLabelPrintForm({ open, warehouseId, selected, scope, onPrinted });
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-none gap-0 overflow-y-auto p-0 sm:max-w-[480px]">
        <SheetHeader className="px-4 pb-3 pt-4 sm:px-6">
          <SheetTitle>Этикетки ячеек</SheetTitle>
          {subtitle ? <SheetDescription>{subtitle}</SheetDescription> : null}
        </SheetHeader>

        {form.result ? (
          <div className="px-4 pb-6 sm:px-6">
            <LabelPrintResult result={form.result} onMore={form.backToForm} onClose={close} />
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col gap-5 px-4 pb-4 sm:px-6">
              <BinLabelPrintFields form={form} selected={selected} rack={scope.rack} />

              <div className="flex flex-col gap-2">
                <SectionLabel as="div">Коды на этикетке</SectionLabel>
                <LabelCodeModeField value={form.codeMode} onChange={form.setCodeMode} hasMini={false} />
              </div>

              <LabelCutLinesField value={form.cutLines} onChange={form.setCutLines} />

              <LabelPrintSummary form={form} />

              <p className="flex items-start gap-1.5 text-xs text-on-surface-variant">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {STICK_HINT}
              </p>

              {form.error ? (
                <Alert severity="error">
                  <div className="flex flex-col gap-2">
                    <span>{form.error.text}</span>
                    {form.error.retryable ? (
                      <Button
                        variant="outline"
                        className="min-h-12 self-start"
                        onClick={() => void form.submit()}
                        disabled={form.submitting}
                      >
                        {LABEL_TEXT.retry}
                      </Button>
                    ) : null}
                  </div>
                </Alert>
              ) : null}
            </div>

            <div className="sticky bottom-0 border-t border-outline-variant bg-surface-lowest px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:px-6">
              <Button
                className="min-h-14 w-full text-base font-medium"
                onClick={() => void form.submit()}
                disabled={!form.canSubmit}
              >
                {form.submitting ? <Loader2 className="size-5 animate-spin" /> : <Printer className="size-5" />}
                <span className="ml-2">{form.submitting ? LABEL_TEXT.submitting : LABEL_TEXT.submit}</span>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
